import React from 'react';
import clsx from 'clsx';
import themePrimary from '../../styles/themePrimary';
import { makeStyles } from '@material-ui/core/styles';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableContainer from '@material-ui/core/TableContainer';
import TableRow from '@material-ui/core/TableRow';
import Paper from '@material-ui/core/Paper';
import { CheckItemFlat } from '../../models/common/CheckItemFlat';
import { CheckOutStateContext } from '../../providers/CheckOutStateContextProvider';
import { String_Check_Discount, String_Check_ServiceCharge, String_Check_SubTotal, String_Check_TaxesAndFees, String_Check_Tip, String_Check_Total, String_Check_TotalPaid, String_Check_VoidedItem } from '../../utilities/StringHelper';
import { ElementDimensions } from '../../styles/ElementDimensions';
import { UserStateContext } from '../../providers/UserStateContextProvider';
import { Tooltip, IconButton, ClickAwayListener } from '@material-ui/core';
import { String_Check_TaxesAndFeesToolTip } from '../../utilities/StringHelper';
import InfoIcon from '@material-ui/icons/Info';

const useStyles = makeStyles({
  quantityCell: {
    border: 'none',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    width: '36px', 
    padding: '2px 12px 2px 4px',
  },
  priceCell: {
    paddingLeft: '0px',
    width: '70px',       // fixed
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    padding: '2px 8px 2px 8px',
    border: 'none',
  },
  iconCell: {
    border: 'none',
    paddingTop: '2px',
    paddingLeft: '3px',
    paddingRight: '3px',
    width: '20px',
  },
  nameCell: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    padding: '2px 8px 2px 0px',
    border: 'none',
  },
  modifierNameCell: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    padding: '2px 8px 2px 24px',
    border: 'none',
  },
  comboModifierNameCell: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    padding: '2px 8px 2px 48px',
    border: 'none',
  },
  totalsNameCell: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    padding: '2px 0px 2px 8px',
    textAlign: 'right',
    border: 'none',
  },
  totalsDividerRow: {
    '& td': {
      position: 'relative',
      paddingTop: '16px',      // space BELOW the line (line -> text)
      '&::before': {
        content: '""',
        position: 'absolute',
        top: '8px',           // space ABOVE the line
        left: 0,
        right: 0,
        borderTop: `1px solid #000000`,
      },
    },
  },
  table: {
    backgroundColor: 'transparent',
    tableLayout: 'fixed',
    marginBottom: '0px',
  },
  tableContainer: {
    overflowX: 'hidden',
    paddingTop: ElementDimensions.padding10 + 'px',
    paddingBottom: ElementDimensions.padding10 + 'px',
    width: 'calc(100% - 10px)',
    marginLeft: 'auto',
    marginRight: 'auto',
    backgroundColor: 'transparent',
  },
  infoIcon: {
    width: '16px',
    height: '16px',
    color: themePrimary.palette.primary.contrastText,
    marginBottom: '3px',
  },
  infoIconContainer: {
    borderRadius: '50px',
    backgroundColor: themePrimary.palette.secondary.contrastText,
    marginRight: '6px',
  },
});

export interface CheckDetailsProps {
  details: CheckItemFlat[];
  isReceipt: boolean;
}

const CheckDetails = (props: CheckDetailsProps) => {
  const { details, isReceipt } = props;
  const [toolTipOpen, setToolTipOpen] = React.useState<boolean>(false);
  const userState = React.useContext(UserStateContext);
  const checkOutState = React.useContext(CheckOutStateContext);
  const classes = useStyles({ primary_main_color: checkOutState.state.skin.primary_main_color, 
      primary_light_color: checkOutState.state.skin.primary_light_color, primary_dark_color: checkOutState.state.skin.primary_dark_color });

  const handleTooltipClose = () => {
      setTimeout(() => {setToolTipOpen(false);}, 2000);
  };

  const handleTooltipOpen = () => {
    setToolTipOpen(true);
  };

  var totalsDividerShown = false;

  return (
    <TableContainer component={Paper} className={classes.tableContainer}>
      <Table aria-label="simple table" className={classes.table}>
        <TableBody>
        {
            details.map((detail, index) => {
                if (detail.isItem)
                {
                    return (
                        <TableRow key={detail.name}>
                            <TableCell align="right" className={classes.quantityCell}>
                                {detail.quantity}
                            </TableCell>
                            <TableCell className={classes.nameCell}>
                                {detail.name}
                            </TableCell>
                            <TableCell align="right" className={classes.priceCell}>
                                {detail.price}
                            </TableCell>
                        </TableRow>
                    );
                }
                else if (detail.isVoidedItem)
                {
                    return (
                        <TableRow key={detail.name}>
                            <TableCell align="right" className={classes.quantityCell}>
                                {detail.quantity}
                            </TableCell>
                            <TableCell className={classes.nameCell}>
                                {detail.name}
                            </TableCell>
                            <TableCell align="right" className={classes.priceCell}>
                                {detail.price}
                            </TableCell>
                        </TableRow>
                    );
                }
                else if (detail.isItemModifier) {
                    return (
                        <TableRow key={detail.name}>
                            <TableCell align="right" className={classes.quantityCell}>
                                {detail.quantity}
                            </TableCell>
                            <TableCell className={classes.modifierNameCell}>
                                {detail.name}
                            </TableCell>
                            <TableCell align="right" className={classes.priceCell}>
                                 {detail.price == "$0.00" ? "" : detail.price}
                            </TableCell>
                        </TableRow>
                    );
                }
                else if (detail.isItemDiscount) {
                    return (
                        <TableRow key={detail.name}>
                            <TableCell align="right" className={classes.quantityCell}>
                                {detail.quantity}
                            </TableCell>
                            <TableCell className={classes.modifierNameCell}>
                                {detail.name}
                            </TableCell>
                            <TableCell align="right" className={classes.priceCell}>
                                {detail.price == "$0.00" ? "" : detail.price}
                            </TableCell>
                        </TableRow>
                    );
                }
                else if (detail.hasComboItems)
                {
                    return (
                        <TableRow key={detail.name}>
                            <TableCell align="right" className={classes.quantityCell}>
                                {detail.quantity}
                            </TableCell>
                            <TableCell className={classes.nameCell}>
                                {detail.name}
                            </TableCell>
                            <TableCell align="right" className={classes.priceCell}>
                                {detail.price == "$0.00" ? "" : detail.price}
                            </TableCell>
                        </TableRow>
                    );
                }
                else if (detail.isComboItem) {
                    return (
                        <TableRow key={detail.name}>
                            <TableCell align="right" className={classes.quantityCell}>
                                {}
                            </TableCell>
                            <TableCell className={classes.modifierNameCell}>
                                {detail.name}
                            </TableCell>
                            <TableCell align="right" className={classes.priceCell}>
                                {detail.price == "$0.00" ? "" : detail.price}
                            </TableCell>
                        </TableRow>
                    );
                }
                else if (detail.isComboItemModifier) {
                    return (
                        <TableRow key={detail.name}>
                            <TableCell align="right" className={classes.quantityCell}>
                                {}
                            </TableCell>
                            <TableCell className={classes.comboModifierNameCell}>
                                {detail.name}
                            </TableCell>
                            <TableCell align="right" className={classes.priceCell}>
                                 {detail.price == "$0.00" ? "" : detail.price}
                            </TableCell>
                        </TableRow>
                    );
                }
                else if (detail.isSubTotal && !totalsDividerShown) {
                    totalsDividerShown = true;
                    return (
                        <TableRow key={detail.name} className={classes.totalsDividerRow}>
                            <TableCell align="right" className={classes.quantityCell}>
                                {detail.quantity}
                            </TableCell>
                            <TableCell align="right" className={classes.totalsNameCell}>
                                {detail.name}:
                            </TableCell>
                            <TableCell align="right" className={classes.priceCell}>
                                {detail.price}
                            </TableCell>
                        </TableRow>
                    );
                }
                else if (detail.isSubTotal && totalsDividerShown) {
                    return (
                        <TableRow key={detail.name}>
                            <TableCell align="right" className={classes.quantityCell}>
                                {detail.quantity}
                            </TableCell>
                            <TableCell align="right" className={classes.totalsNameCell}>
                                {detail.name}:
                            </TableCell>
                            <TableCell align="right" className={classes.priceCell}>
                                {detail.price}
                            </TableCell>
                        </TableRow>
                    );
                }
                else if (detail.isServiceCharge) {
                    return (
                        <TableRow key={detail.name}>
                            <TableCell align="right" className={classes.quantityCell}>
                                {detail.quantity}
                            </TableCell>
                            <TableCell align="right" className={classes.totalsNameCell}>
                                {(detail.name !== undefined && detail.name !== null && detail.name !== "") &&
                                    detail.name
                                }
                                {(detail.name === undefined || detail.name === null || detail.name === "") &&
                                    String_Check_ServiceCharge.find(s => s.key === userState.state.locale)!!.value
                                }
                                :
                            </TableCell>
                            <TableCell align="right" className={classes.priceCell}>
                                {detail.price}
                            </TableCell>
                        </TableRow>
                    );
                }
                else if (detail.isDiscount && !totalsDividerShown) {
                    totalsDividerShown = true;
                    return (
                        <TableRow key={detail.name} className={classes.totalsDividerRow}>
                            <TableCell align="right" className={classes.quantityCell}>
                                {detail.quantity}
                            </TableCell>
                            <TableCell align="right" className={classes.totalsNameCell}>
                                {detail.name}:
                            </TableCell>
                            <TableCell align="right" className={classes.priceCell}>
                                {detail.price}
                            </TableCell>
                        </TableRow>
                    );
                }
                else if (detail.isDiscount && totalsDividerShown) {
                    return (
                        <TableRow key={detail.name}>
                            <TableCell align="right" className={classes.quantityCell}>
                                {detail.quantity}
                            </TableCell>
                            <TableCell align="right" className={classes.totalsNameCell}>
                                {detail.name}:
                            </TableCell>
                            <TableCell align="right" className={classes.priceCell}>
                                {detail.price}
                            </TableCell>
                        </TableRow>
                    );
                }
                else if (detail.isTotal) {
                    return (
                        <TableRow key={detail.name}>
                            <TableCell align="right" className={classes.quantityCell}>
                                {detail.quantity}
                            </TableCell>
                            <TableCell align="right" className={classes.totalsNameCell}>
                                {detail.name}:
                            </TableCell>
                            <TableCell align="right" className={classes.priceCell}>
                                {detail.price}
                            </TableCell>
                        </TableRow>
                    );
                }
                else if (detail.isTip) {
                  return (
                      <TableRow key={detail.name}>
                          <TableCell align="right" className={classes.quantityCell}>
                              {detail.quantity}
                          </TableCell>
                          <TableCell align="right" className={classes.totalsNameCell}>
                            {detail.name}:
                          </TableCell>
                          <TableCell align="right" className={classes.priceCell}>
                              {detail.price}
                            </TableCell>
                      </TableRow>
                  );
                }
                else if (detail.isPaidTotal) {
                  return (
                      <TableRow key={detail.name}>
                          <TableCell align="right" className={classes.quantityCell}>
                              {detail.quantity}
                          </TableCell>
                          <TableCell align="right" className={classes.totalsNameCell}>
                                {detail.name}:
                          </TableCell>
                          <TableCell align="right" className={classes.priceCell}>
                              {detail.price}
                          </TableCell>
                      </TableRow>
                  );
                }
                else if (detail.isSplitTotal && (detail.name.indexOf('100') === -1)) {
                  return (
                      <TableRow key={detail.name}>
                          <TableCell align="right" className={classes.quantityCell}>
                              {detail.quantity}
                          </TableCell>
                          <TableCell align="right" className={classes.totalsNameCell}>
                              {detail.name}:
                          </TableCell>
                          <TableCell align="right" className={classes.priceCell}>
                              {detail.price}
                            </TableCell>
                      </TableRow>
                  );
                }
                else if (detail.isTax) {
                    return (
                        <TableRow key={detail.name}>
                            <TableCell align="right" className={classes.quantityCell}>
                                {detail.quantity}
                            </TableCell>
                            <TableCell align="right" className={classes.totalsNameCell}>
                                <div>
                                    {detail.name.toLowerCase().indexOf("fees") != -1 &&
                                     <ClickAwayListener onClickAway={handleTooltipClose}>
                                        <Tooltip
                                            arrow
                                            disableHoverListener
                                            disableFocusListener
                                            onClose={handleTooltipClose}
                                            open={toolTipOpen}
                                            title={String_Check_TaxesAndFeesToolTip(checkOutState.state.digitalServiceFee!!).find(s => s.key === userState.state.locale)!!.value}>
                                            <span className={classes.infoIconContainer}>
                                                <InfoIcon onClick={handleTooltipOpen} className={classes.infoIcon} />
                                            </span>
                                        </Tooltip>
                                    </ClickAwayListener>
                                    }
                                    {detail.name}:
                                </div>
                            </TableCell>
                            <TableCell align="right" className={classes.priceCell}>
                                {detail.price}
                            </TableCell>
                        </TableRow>
                    );
                }
            })
        }
        </TableBody>
      </Table>
    </TableContainer>
  );
}

export default CheckDetails;